/**
 * "Always see every outfit" for the Desktop mascot gallery card (FullOutfitAccessSection.tsx): who sees every outfit at
 * all times (owner, 2026-10-09: "MY ACCOUNT should show every outfit avaiable all the time"; "for all the time all outfit
 * access lets use security group USER-PRINCIPALS"), the service's rules for an e-mail on the list and a group's object id
 * (services/gyro-svc/src/desktopMascotFullAccessRules.ts, which this file is tested against), what Save sends (PUT
 * /v1/admin/desktop/mascot/full-access), and its wording.
 *
 * No React, no network.
 */

/** The most e-mails the list holds. */
export const FULL_ACCESS_EMAILS_MAX = 50;
/** The longest e-mail address the list takes. */
export const FULL_ACCESS_EMAIL_MAX = 254;
/** An e-mail as the list keeps it: lower case, one @, a dot in the domain, no spaces or control characters. */
export const FULL_ACCESS_EMAIL_RE = /^[^\s@\u0000-\u001f\u007f]+@[^\s@\u0000-\u001f\u007f]+\.[^\s@\u0000-\u001f\u007f]+$/;
/** An Entra group's object id as the setting keeps it: a GUID, lower case. */
export const GROUP_OBJECT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** An e-mail as the list compares it: trimmed, lower case. */
export function normalizeFullAccessEmail(value: string): string {
  return value.trim().toLowerCase();
}

/** Whether a value is an e-mail the list can keep, as kept (trimmed, lower case). The service's rule. */
export function isFullAccessEmail(value: unknown): value is string {
  return typeof value === 'string' && value.length <= FULL_ACCESS_EMAIL_MAX && value === normalizeFullAccessEmail(value) && FULL_ACCESS_EMAIL_RE.test(value);
}

/** A group object id as the setting keeps it: trimmed, lower case. */
export function normalizeGroupObjectId(value: string): string {
  return value.trim().toLowerCase();
}

/** Whether a value is a group object id as kept: a lower-case GUID. The service's rule. */
export function isGroupObjectId(value: unknown): value is string {
  return typeof value === 'string' && GROUP_OBJECT_ID_RE.test(value);
}

/** Whether the group is in effect now, as the service says (desktopMascotFullAccess.ts FullAccessGroupStatus). */
export type FullAccessGroupState = 'none' | 'waiting-for-bridge' | 'not-read-yet' | 'refused' | 'in-effect';

export interface FullAccessGroupStatus {
  state: FullAccessGroupState;
  inEffect: boolean;
  /** In effect: how many members The Bridge answered. */
  members?: number;
  /** When the group was last read (in effect) or refused. */
  readAtMs?: number;
  /** Refused: the refusal's code. */
  reason?: string;
  /** Waiting for The Bridge: the service's sentence about its Bridge connection. */
  detail?: string;
}

/** GET and PUT /v1/admin/desktop/mascot/full-access. */
export interface FullAccessView {
  /** The revision every save sends back as expectedRevision; a stale one is refused with 409 mascot_full_access_changed. */
  revision: number;
  emails: string[];
  groupId: string | null;
  group: FullAccessGroupStatus;
  limits: { emails: number };
}

export interface SaveFullAccessBody {
  emails: string[];
  groupId: string | null;
  expectedRevision: number;
}

/** Why an e-mail typed into Add would be refused, in plain words, or null when it can be added. */
export function emailAddRefusal(value: string, listed: readonly string[]): string | null {
  const email = normalizeFullAccessEmail(value);
  if (email === '') return 'Type the person’s e-mail address.';
  if (!isFullAccessEmail(email)) return `“${value.trim().slice(0, 80)}” is not an e-mail address. Type it as name@exante360.com.`;
  if (listed.includes(email)) return `${email} is already listed.`;
  if (listed.length >= FULL_ACCESS_EMAILS_MAX) return `The list holds at most ${FULL_ACCESS_EMAILS_MAX} people. Use the group for more.`;
  return null;
}

/** Why the group id as typed would be refused, in plain words, or null (an empty field means no group). */
export function groupIdRefusal(value: string): string | null {
  const id = normalizeGroupObjectId(value);
  if (id === '' || isGroupObjectId(id)) return null;
  return 'Set the group by its object id, a GUID such as 0f8fad5b-d9cb-469f-a165-70867728950e (Entra admin center, Groups, the group, Object ID).';
}

/** The group as the setting keeps it: its object id, or null for an empty field. */
export function groupIdOf(value: string): string | null {
  const id = normalizeGroupObjectId(value);
  return id === '' ? null : id;
}

/** The PUT body for Save: the list as the section holds it, the group from its field, the revision it loaded. */
export function fullAccessSaveBody(emails: readonly string[], groupInput: string, expectedRevision: number): SaveFullAccessBody {
  return { emails: emails.map(normalizeFullAccessEmail), groupId: groupIdOf(groupInput), expectedRevision };
}

/** Whether the section's list or group differ from what the service holds (so Save is offered). */
export function fullAccessChanged(emails: readonly string[], groupInput: string, stored: Pick<FullAccessView, 'emails' | 'groupId'>): boolean {
  if (groupIdOf(groupInput) !== stored.groupId) return true;
  return emails.length !== stored.emails.length || emails.some((email, index) => normalizeFullAccessEmail(email) !== stored.emails[index]);
}

/** A refusal code from the service, in words. */
function refusalWords(reason: string | undefined): string {
  switch (reason) {
    case 'forbidden':
      return 'The Bridge did not let gyro-svc read the group (no grant for it yet, the group not in its scope there, or its sign-in not accepted)';
    case 'identity_unverified':
    case 'identity_mismatch':
      return 'The Bridge could not confirm it was gyro-svc asking';
    case 'group_deleted':
      return 'The Bridge says the group is deleted';
    case 'other_group':
    case 'malformed':
      return 'The Bridge’s answer could not be read as the group’s members';
    case 'not_found':
      return 'The Bridge does not know the group';
    case 'unavailable':
      return 'The Bridge could not answer just now';
    default:
      return 'The Bridge refused it';
  }
}

/** When the service read the group, as the section says it (" at 3:04 PM"; the browser's zone unless one is given). */
function readAt(ms: number | undefined, timeZone?: string): string {
  if (ms === undefined) return '';
  // From the parts, so the words never depend on the space a browser puts before AM and PM.
  const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...(timeZone ? { timeZone } : {}) }).formatToParts(new Date(ms));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? '';
  return ` at ${part('hour')}:${part('minute')} ${part('dayPeriod')}`;
}

/** What the group's line says: whether it is in effect now, and why not. */
export function groupStatusText(status: FullAccessGroupStatus, options: { timeZone?: string } = {}): string {
  switch (status.state) {
    case 'none':
      return 'No group is set: only the people listed above.';
    case 'waiting-for-bridge':
      return 'Not in effect yet. The group takes effect once The Bridge connection for gyro-svc is live (the-bridge#760); until then only the people listed above see every outfit.';
    case 'not-read-yet':
      return 'Not in effect yet: Gyro has not had the group’s members from The Bridge yet. Reload in a minute.';
    case 'refused':
      return `Not in effect: ${refusalWords(status.reason)}${readAt(status.readAtMs, options.timeZone)}, so only the people listed above see every outfit. Gyro asks again every minute.`;
    case 'in-effect': {
      const members = status.members ?? 0;
      return `In effect: ${members} member${members === 1 ? '' : 's'}, read from The Bridge${readAt(status.readAtMs, options.timeZone)}. Changes to the group show within about 15 minutes.`;
    }
  }
}

/** The section's explanation, above the list. */
export const FULL_ACCESS_HINT = 'The people listed here, and the members of the group below, see every outfit in Gyro’s outfits at all times, whatever its dates, its category’s switch or season, or its day, and can wear any of them on any day. Retired outfits stay hidden. Everyone else sees the gallery as usual.';
export const FULL_ACCESS_SAVED_TEXT = 'Saved. They see every outfit the next time Gyro’s outfits opens or the desktop checks in (about 15 minutes).';
export const FULL_ACCESS_CHANGED_TEXT = 'Another admin changed who always sees every outfit after you loaded it, so nothing was saved. It is reloaded below: check it and make your change again.';
export const FULL_ACCESS_UNREADABLE_TEXT = 'Gyro could not read who always sees every outfit just now, so it cannot be changed here. Reload in a minute.';
